/**
 * NavBookValueBadge — pill shown near the price at the top of the
 * Stock Analysis page.  Displays:
 *   • ETFs   → Live iNAV per share + % premium/discount to iNAV.  The
 *              iNAV is recomputed every 30 s during regular market hours
 *              from the underlying holdings' current prices via the
 *              /api/stock/inav-live/{ticker} polling endpoint.
 *   • Stocks → Book Value per share + % premium/discount to book.
 *
 * States:
 *   • loading  → skeleton pill with shimmering placeholders
 *   • error    → dim pill with "iNAV failed" + refresh button
 *   • ready    → value + anchor + calc + refresh button
 *
 * Green = trading below fair-value proxy (potential discount),
 * Red   = trading above (potential premium),
 * Amber = within ±0.5% of fair value.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Scale, Activity, RefreshCw, AlertTriangle } from "lucide-react";
import { API } from "../config/api";

export default function NavBookValueBadge({ ticker }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // 'loading' | 'ready' | 'error'
  const [refreshKey, setRefreshKey] = useState(0);
  const pollTimer = useRef(null);

  // Initial (full) fetch when the ticker changes or on manual refresh.
  // Auto-retries up to 3 times with exponential backoff so transient
  // Yahoo rate-limit failures on production don't leave the badge blank.
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setStatus("loading");
    setData(null);

    const attemptFetch = async (attempt) => {
      try {
        const r = await axios.get(
          `${API}/stock/nav-book-value/${encodeURIComponent(ticker)}`,
          { timeout: 20000 }
        );
        if (cancelled) return;
        if (r?.data && r.data.value_per_share != null) {
          setData(r.data);
          setStatus("ready");
          return;
        }
        throw new Error("empty payload");
      } catch (_) {
        if (cancelled) return;
        if (attempt < 3) {
          // Exponential backoff: 1s → 2s → 4s
          await new Promise((res) => setTimeout(res, 1000 * (2 ** (attempt - 1))));
          if (!cancelled) attemptFetch(attempt + 1);
        } else {
          setStatus("error");
        }
      }
    };
    attemptFetch(1);
    return () => { cancelled = true; };
  }, [ticker, refreshKey]);

  // Live iNAV polling — every 30 s while the market is open + the ticker
  // is an ETF for which we successfully priced the basket.  Uses the
  // lightweight /inav-live/{ticker} endpoint to avoid re-fetching static
  // book/NAV values.
  useEffect(() => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (!data || data.type !== "ETF" || !data.inav_live) return;
    if (data.market_state !== "REGULAR" && data.market_state !== "PRE" && data.market_state !== "POST") return;

    const poll = async () => {
      try {
        const live = await axios.get(
          `${API}/stock/inav-live/${encodeURIComponent(ticker)}`,
          { timeout: 12000 }
        );
        const inav = live.data;
        setData((d) => d ? {
          ...d,
          value_per_share: inav?.inav_live ?? d.value_per_share,
          inav_live: inav?.inav_live ?? d.inav_live,
          basket_change_pct: inav?.basket_change_pct ?? d.basket_change_pct,
          coverage_pct: inav?.coverage_pct ?? d.coverage_pct,
          n_holdings_priced: inav?.n_holdings ?? d.n_holdings_priced,
          market_state: inav?.market_state ?? d.market_state,
          current_price: inav?.current_price ?? d.current_price,
          premium_pct: inav?.premium_pct ?? d.premium_pct,
          sec_filing: inav?.sec_filing ?? d.sec_filing,
          calculated_inav: inav?.calculated_inav ?? d.calculated_inav,
          calc_anchor_nav: inav?.calc_anchor_nav ?? d.calc_anchor_nav,
          calc_basket_change_pct: inav?.calc_basket_change_pct ?? d.calc_basket_change_pct,
          calc_coverage_pct: inav?.calc_coverage_pct ?? d.calc_coverage_pct,
          calc_n_holdings: inav?.calc_n_holdings ?? d.calc_n_holdings,
          computed_at: inav?.fetched_at ?? d.computed_at,
        } : d);
      } catch (_) { /* keep last known */ }
    };
    pollTimer.current = setInterval(poll, 30000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [data, ticker]);

  const handleRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  if (!ticker) return null;

  // ── LOADING skeleton ─────────────────────────────────────────────────
  if (status === "loading") {
    return (
      <div
        className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-slate-700 bg-slate-900/60 text-[11px] font-medium animate-pulse"
        data-testid="nav-book-value-skeleton"
        title="Loading fair value…"
      >
        <Scale className="w-3 h-3 text-slate-500" />
        <span className="uppercase tracking-wider text-[9px] text-slate-500">iNAV</span>
        <span className="inline-block h-3 w-14 rounded bg-slate-700/70" />
        <span className="text-[9px] opacity-40">|</span>
        <span className="uppercase tracking-wider text-[9px] text-slate-500">Anchor</span>
        <span className="inline-block h-3 w-12 rounded bg-slate-700/70" />
        <span className="text-[9px] opacity-40">|</span>
        <span className="uppercase tracking-wider text-[9px] text-slate-500">Calc</span>
        <span className="inline-block h-3 w-12 rounded bg-slate-700/70" />
        <span className="text-[10px] text-slate-500">loading…</span>
      </div>
    );
  }

  // ── ERROR state ──────────────────────────────────────────────────────
  if (status === "error") {
    return (
      <div
        className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-slate-700 bg-slate-900/60 text-[11px] font-medium text-slate-400"
        data-testid="nav-book-value-error"
        title="Fair-value data failed to load. Click refresh to retry."
      >
        <AlertTriangle className="w-3 h-3 text-amber-400" />
        <span className="uppercase tracking-wider text-[9px] text-slate-400">iNAV</span>
        <span className="text-[11px]">unavailable</span>
        <button
          type="button"
          onClick={handleRefresh}
          data-testid="nav-book-value-refresh"
          title="Retry loading iNAV"
          className="ml-1 inline-flex items-center gap-1 rounded border border-slate-600 bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold text-slate-100 hover:bg-slate-700 hover:text-white transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      </div>
    );
  }

  // ── READY ────────────────────────────────────────────────────────────
  const isEtf = data.type === "ETF";
  const p = data.premium_pct;
  let stateClass = "text-amber-300 border-amber-700 bg-amber-950/40";
  let stateLabel = "at fair value";
  if (p != null) {
    if (p <= -0.5) {
      stateClass = "text-emerald-300 border-emerald-700 bg-emerald-950/40";
      stateLabel = `${Math.abs(p).toFixed(2)}% discount`;
    } else if (p >= 0.5) {
      stateClass = "text-rose-300 border-rose-700 bg-rose-950/40";
      stateLabel = `${p.toFixed(2)}% premium`;
    } else {
      stateLabel = `${p >= 0 ? "+" : ""}${p.toFixed(2)}% vs ${isEtf ? "NAV" : "Book"}`;
    }
  }

  const isLive = isEtf && data.inav_live != null && data.market_state === "REGULAR";
  const label = isEtf
    ? (isLive ? "Live iNAV" : (data.market_state === "PRE" || data.market_state === "POST" ? "iNAV" : "NAV"))
    : "Book value";

  const tooltip = isEtf && data.inav_live
    ? `Live intraday NAV computed from ${data.n_holdings_priced} underlying holdings (${(data.coverage_pct ?? 0).toFixed(1)}% weight coverage).\n`
      + `Yesterday's struck NAV: $${(data.nav_close ?? 0).toFixed(2)}\n`
      + `Basket move today: ${(data.basket_change_pct ?? 0) >= 0 ? "+" : ""}${(data.basket_change_pct ?? 0).toFixed(2)}%\n`
      + (data.sec_filing
          ? `SEC cash − liab layer: ${data.sec_filing.per_share_adjustment >= 0 ? "+" : ""}$${data.sec_filing.per_share_adjustment.toFixed(4)}/share `
            + `(from ${data.sec_filing.form} filed ${data.sec_filing.date})\n`
          : "")
      + `Market state: ${data.market_state ?? "?"}\n`
      + (data.computed_at ? `Updated: ${new Date(data.computed_at).toLocaleTimeString()}` : "")
    : (data.source || "");

  return (
    <div
      className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-[11px] font-medium ${stateClass}`}
      data-testid="nav-book-value-badge"
      title={tooltip}
    >
      {isLive
        ? <Activity className="w-3 h-3 animate-pulse" data-testid="nav-live-pulse" />
        : <Scale className="w-3 h-3" />}
      <span className="uppercase tracking-wider text-[9px] opacity-80">
        {label}
      </span>
      <span className="font-mono font-bold" data-testid="nav-book-value-amount">
        ${Number(data.value_per_share).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </span>
      {/* Anchor NAV — yesterday's struck NAV.  Shown as context so the
          user can see where the fund closed vs. where the basket has
          moved intraday. */}
      {isEtf && data.nav_close != null && (
        <>
          <span className="text-[9px] opacity-50">|</span>
          <span className="uppercase tracking-wider text-[9px] opacity-60">Anchor</span>
          <span
            className="font-mono font-semibold text-slate-200"
            data-testid="nav-anchor-value"
            title="Yesterday's struck NAV (anchor for iNAV computation)"
          >
            ${Number(data.nav_close).toFixed(2)}
          </span>
        </>
      )}
      {/* Calculated iNAV — weight-based basket ratio (3rd method).
          Fallback that always renders when the primary iNAV pipeline
          is missing share counts or SEC data (e.g. SMH, XLK, ARKK). */}
      {isEtf && data.calculated_inav != null && (
        <>
          <span className="text-[9px] opacity-50">|</span>
          <span className="uppercase tracking-wider text-[9px] opacity-60">Calc</span>
          <span
            className="font-mono font-semibold text-amber-300"
            data-testid="nav-calc-value"
            title={
              `Calc iNAV (3rd method) = anchor × Σ(wⱼ × pⱼ_now) / Σ(wⱼ × pⱼ_prev)\n`
              + `Weight-based basket ratio — fallback when Primary fails.\n`
              + `Holdings priced: ${data.calc_n_holdings ?? 0} (${(data.calc_coverage_pct ?? 0).toFixed(1)}% coverage)\n`
              + `Basket move today: ${(data.calc_basket_change_pct ?? 0) >= 0 ? "+" : ""}${(data.calc_basket_change_pct ?? 0).toFixed(2)}%`
            }
          >
            ${Number(data.calculated_inav).toFixed(2)}
          </span>
        </>
      )}
      <span className="text-[10px]">·</span>
      <span className="text-[10px]" data-testid="nav-book-value-state">{stateLabel}</span>
      {isEtf && data.basket_change_pct != null && (
        <span
          className={`text-[10px] font-mono ml-1 ${data.basket_change_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}
          data-testid="nav-basket-change"
        >
          bskt {data.basket_change_pct >= 0 ? "+" : ""}{data.basket_change_pct.toFixed(2)}%
        </span>
      )}
      {/* Manual refresh — force a re-fetch of the /nav-book-value +
          /inav-live pair.  Handy on production when the initial load
          times out due to Yahoo rate limits. */}
      <button
        type="button"
        onClick={handleRefresh}
        data-testid="nav-book-value-refresh"
        title="Refresh iNAV now"
        className="ml-1 inline-flex items-center justify-center rounded border border-current/40 bg-black/20 p-0.5 opacity-70 hover:opacity-100 hover:bg-black/40 transition-opacity"
      >
        <RefreshCw className="w-3 h-3" />
      </button>
    </div>
  );
}
